import * as XLSX from "xlsx";
import { ParseDate } from "app/utils/helper";

const DATE_KEY_PATTERN = /date|dob/i;

export function flattenObject(obj, parentKey = "", result = {}) {
  if (!obj || typeof obj !== "object") return result;

  Object.keys(obj).forEach((key) => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    const value = obj[key];

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      flattenObject(value, newKey, result);
    } else {
      result[newKey] = value;
    }
  });

  return result;
}

export function getNestedValue(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function updateStudentField(students, rowIndex, path, value) {
  return students.map((row, index) => {
    if (index !== rowIndex) return row;

    const keys = path.split(".");
    if (keys.length === 1) {
      return { ...row, [path]: value };
    }

    const newRow = { ...row };
    let cursor = newRow;

    keys.slice(0, -1).forEach((key) => {
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    });

    cursor[keys[keys.length - 1]] = value;
    return newRow;
  });
}

export function deleteColumnFromStudents(students, columnKey) {
  const keys = columnKey.split(".");

  return students.map((row) => {
    const newRow = { ...row };

    if (keys.length === 1) {
      delete newRow[columnKey];
      return newRow;
    }

    let nestedObj = newRow;
    for (let i = 0; i < keys.length - 1; i += 1) {
      if (!nestedObj[keys[i]]) break;

      if (i === keys.length - 2) {
        nestedObj[keys[i]] = { ...nestedObj[keys[i]] };
        delete nestedObj[keys[i]][keys[i + 1]];

        if (Object.keys(nestedObj[keys[i]]).length === 0) {
          delete nestedObj[keys[i]];
        }
      } else {
        nestedObj[keys[i]] = { ...nestedObj[keys[i]] };
        nestedObj = nestedObj[keys[i]];
      }
    }

    return newRow;
  });
}

export function stringifyColumnInStudents(students, columnKey) {
  const keys = columnKey.split(".");

  return students.map((row) => {
    const newRow = { ...row };

    if (keys.length === 1) {
      newRow[columnKey] = String(newRow[columnKey] ?? "");
      return newRow;
    }

    let nestedObj = newRow;
    for (let i = 0; i < keys.length - 1; i += 1) {
      if (!nestedObj[keys[i]]) break;

      if (i === keys.length - 2) {
        nestedObj[keys[i]] = { ...nestedObj[keys[i]] };
        nestedObj[keys[i]][keys[i + 1]] = String(
          nestedObj[keys[i]][keys[i + 1]] ?? ""
        );
      } else {
        nestedObj[keys[i]] = { ...nestedObj[keys[i]] };
        nestedObj = nestedObj[keys[i]];
      }
    }

    return newRow;
  });
}

function reconstructRow(row) {
  const reconstructedRow = {};

  Object.keys(row).forEach((key) => {
    const keys = key.split(/\.|\[|\]/).filter(Boolean);
    let temp = reconstructedRow;

    for (let i = 0; i < keys.length - 1; i += 1) {
      if (!Number.isNaN(Number(keys[i + 1]))) {
        temp = temp[keys[i]] = temp[keys[i]] || [];
      } else {
        temp = temp[keys[i]] = temp[keys[i]] || {};
      }
    }

    const leafKey = keys[keys.length - 1];
    temp[leafKey] = DATE_KEY_PATTERN.test(leafKey)
      ? ParseDate(row[key])
      : row[key];
  });

  return reconstructedRow;
}

export function getColumnKeysFromStudents(students = []) {
  if (!students.length) return [];
  return Object.keys(flattenObject(students[0] || {}));
}

export function isAcceptedUploadFile(file) {
  if (!file?.name) return false;

  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return [".xlsx", ".xls", ".csv"].includes(extension);
}

function parseWorkbookRows(workbook) {
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

  return json.map(reconstructRow);
}

export function parseSpreadsheetFile(file, content) {
  const isCsv = file.name.toLowerCase().endsWith(".csv");
  const workbook = isCsv
    ? XLSX.read(content, { type: "string" })
    : XLSX.read(new Uint8Array(content), { type: "array" });

  return parseWorkbookRows(workbook);
}

export function readAndParseUploadFile(file) {
  return new Promise((resolve, reject) => {
    if (!isAcceptedUploadFile(file)) {
      reject(new Error("Unsupported file type"));
      return;
    }

    const reader = new FileReader();
    const isCsv = file.name.toLowerCase().endsWith(".csv");

    reader.onload = (event) => {
      try {
        resolve(parseSpreadsheetFile(file, event.target.result));
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));

    if (isCsv) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  });
}

export function parseExcelFile(arrayBuffer) {
  const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
  return parseWorkbookRows(workbook);
}

export function downloadSampleExcel(jsonData, sampleFileName) {
  const flattenedData = (jsonData || []).map((item) => flattenObject(item));
  const worksheet = XLSX.utils.json_to_sheet(flattenedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sampleFileName);
  XLSX.writeFile(workbook, `${sampleFileName}.xlsx`);
}

export function aggregateBulkRegisterResponse(data = []) {
  return data.reduce(
    (acc, item) => ({
      failedCount: acc.failedCount + (item?.failedCount || 0),
      successCount: acc.successCount + (item?.successCount || 0),
      failedList: acc.failedList.concat(item?.failedList || []),
      successList: acc.successList.concat(item?.successList || []),
      message: acc.message.concat(item?.message || []),
    }),
    {
      failedCount: 0,
      successCount: 0,
      failedList: [],
      successList: [],
      message: [],
    }
  );
}

export function formatSerialNumber(index) {
  const serial = index + 1;
  return serial < 10 ? `0${serial}` : String(serial);
}

export function formatColumnHeader(columnKey) {
  const formatted = columnKey
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());

  return formatted.length > 20 ? `${formatted.slice(0, 20)}...` : formatted;
}

export function buildFieldErrorMap(validationErrors = []) {
  const map = new Map();

  validationErrors.forEach((error) => {
    if (error?.path) {
      map.set(error.path, error.message);
    }
  });

  return map;
}

export function parseValidationErrorPath(path) {
  if (!path) return null;

  const match = path.match(/^students\[(\d+)\]\.(.+)$/);
  if (!match) return null;

  return {
    rowIndex: Number.parseInt(match[1], 10),
    columnKey: match[2],
  };
}

export function formatValidationErrorLabel(error) {
  const parsed = parseValidationErrorPath(error?.path);

  if (!parsed) {
    return error?.message || "Validation error";
  }

  return `Row ${parsed.rowIndex + 1}, ${parsed.columnKey}: ${error.message}`;
}

export function getCellDomId(rowIndex, columnKey) {
  return `bulk-cell-${rowIndex}-${columnKey.replace(/\./g, "--")}`;
}

export function getCellErrorMessage(errorMap, rowIndex, columnKey) {
  return errorMap.get(`students[${rowIndex}].${columnKey}`);
}
